import React, { useEffect, useState, useRef } from 'react';
import { useNavStep } from '../lib/NavHistory';
import {
  CreditCard, Users, FileText, RefreshCw, DollarSign,
  CheckCircle, XCircle, Loader2, Search, ChevronRight,
  Zap, TrendingUp, RotateCcw, ShoppingCart,
  Wallet, BarChart3, Package, Link2, Clock, ExternalLink,
  Shield, Fingerprint, AlertTriangle, FileBarChart, Percent,
  UserCheck, Eye, Ban, Plus
} from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import DraggableTabBar from '../components/DraggableTabBar';
import '../styles/StripePage.css';
import PrintButton from '../components/PrintButton';

function fmt(cents) { return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }); }
function fmtD(ts) { if (!ts) return '—'; return new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleDateString(); }
function fmtDT(ts) { if (!ts) return '—'; return new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleString(); }

function StripePage({ user }) {
  const [tab, setTab] = useState('dashboard');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connErr, setConnErr] = useState('');
  const tabsRef = useRef(null);
  const { onKeyDown: handleTabKeyDown } = useArrowNav(tabsRef, 'button', { vertical: false, activateOnFocus: true });

  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [custMore, setCustMore] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [custSearchRes, setCustSearchRes] = useState(null);
  const [charges, setCharges] = useState([]);
  const [chargesMore, setChargesMore] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invMore, setInvMore] = useState(false);
  const [invFilter, setInvFilter] = useState('');
  const [subs, setSubs] = useState([]);
  const [subsMore, setSubsMore] = useState(false);
  const [subFilter, setSubFilter] = useState('');
  const [refunds, setRefunds] = useState([]);
  const [refMore, setRefMore] = useState(false);
  const [products, setProducts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payMore, setPayMore] = useState(false);

  // New sections
  const [taxRates, setTaxRates] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [verMore, setVerMore] = useState(false);
  const [verFilter, setVerFilter] = useState('');
  const [disputes, setDisputes] = useState([]);
  const [dispMore, setDispMore] = useState(false);
  const [fraudWarnings, setFraudWarnings] = useState([]);
  const [balTxns, setBalTxns] = useState([]);
  const [balMore, setBalMore] = useState(false);
  const [balType, setBalType] = useState('');
  const [reportTypes, setReportTypes] = useState([]);
  const [reportRuns, setReportRuns] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [custProfile, setCustProfile] = useState({ charges: [], invoices: [], subs: [], methods: [] });

  // #65 Phase C2b-1: the customer profile is this page's master->detail sub-view.
  // Register ONE Back step so global Back returns to the customers LIST before
  // navigating away — same handler the in-view "← Back to Customers" button uses.
  // Unconditional, top-level (no early returns precede it). LITERAL label — never
  // a customer name/email/id (no PHI in the sessionStorage-backed nav stack).
  useNavStep({
    active: tab === 'customers' && !!selectedCustomer,
    label: 'Customer',
    onBack: () => setSelectedCustomer(null),
  });

  const [loading, setLoading] = useState({});
  const setL = (k, v) => setLoading(p => ({ ...p, [k]: v }));

  useEffect(() => { testConn(true); }, []);

  async function testConn(silent = false) {
    if (!silent) setConnecting(true);
    setConnErr('');
    try {
      const r = await window.zignagenixAPI.stripeTestConnection();
      if (r?.success) setConnected(true);
      else { setConnected(false); if (!silent) setConnErr(r?.error || 'Connection failed'); }
    } catch (e) { setConnected(false); if (!silent) setConnErr(e.message); }
    finally { setConnecting(false); }
  }

  // ── Loaders ────────────────────────────────────────────────────────
  async function loadDash() { setL('dash', true); try { const r = await window.zignagenixAPI.stripeGetDashboardSummary(); if (r?.success) setSummary(r.summary); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('dash', false); } }
  async function loadCust(sa) { setL('cust', true); try { const r = await window.zignagenixAPI.stripeGetCustomers(25, sa); if (r?.success) { setCustomers(r.customers); setCustMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('cust', false); } }
  async function searchCust() { if (!custSearch.trim()) { setCustSearchRes(null); return; } setL('custS', true); try { const r = await window.zignagenixAPI.stripeSearchCustomers(custSearch.trim()); setCustSearchRes(Array.isArray(r?.customers) ? r.customers : []); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('custS', false); } }
  async function loadCharges(sa) { setL('chg', true); try { const r = await window.zignagenixAPI.stripeGetCharges(25, sa); if (r?.success) { setCharges(r.charges); setChargesMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('chg', false); } }
  async function loadInv(sa, st) { setL('inv', true); try { const r = await window.zignagenixAPI.stripeGetInvoices(25, sa, st || undefined); if (r?.success) { setInvoices(r.invoices); setInvMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('inv', false); } }
  async function loadSubs(sa, st) { setL('sub', true); try { const r = await window.zignagenixAPI.stripeGetSubscriptions(25, sa, st || undefined); if (r?.success) { setSubs(r.subscriptions); setSubsMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('sub', false); } }
  async function loadRef(sa) { setL('ref', true); try { const r = await window.zignagenixAPI.stripeGetRefunds(25, sa); if (r?.success) { setRefunds(r.refunds); setRefMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('ref', false); } }
  async function loadProd() { setL('prod', true); try { const r = await window.zignagenixAPI.stripeGetProducts(50); if (r?.success) setProducts(r.products); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('prod', false); } }
  async function loadPay(sa) { setL('pay', true); try { const r = await window.zignagenixAPI.stripeGetPayouts(25, sa); if (r?.success) { setPayouts(r.payouts); setPayMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('pay', false); } }
  async function loadTax() { setL('tax', true); try { const r = await window.zignagenixAPI.stripeGetTaxRates(); if (r?.success) setTaxRates(r.taxRates); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('tax', false); } }
  async function loadVer(st) { setL('ver', true); try { const r = await window.zignagenixAPI.stripeListVerifications(25, st || undefined); if (r?.success) { setVerifications(r.sessions); setVerMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('ver', false); } }
  async function loadDisp(sa) { setL('disp', true); try { const r = await window.zignagenixAPI.stripeGetDisputes(25, sa); if (r?.success) { setDisputes(r.disputes); setDispMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('disp', false); } }
  async function loadFraud() { setL('fraud', true); try { const r = await window.zignagenixAPI.stripeGetFraudWarnings(25); if (r?.success) setFraudWarnings(r.warnings); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('fraud', false); } }
  async function loadBal(sa, tp) { setL('bal', true); try { const r = await window.zignagenixAPI.stripeGetBalanceTransactions(25, sa, tp || undefined); if (r?.success) { setBalTxns(r.transactions); setBalMore(r.hasMore); } } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('bal', false); } }
  async function loadRepTypes() { setL('repT', true); try { const r = await window.zignagenixAPI.stripeGetReportTypes(); if (r?.success) setReportTypes(r.reportTypes); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('repT', false); } }
  async function loadRepRuns() { setL('repR', true); try { const r = await window.zignagenixAPI.stripeGetReportRuns(20); if (r?.success) setReportRuns(r.reportRuns); } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('repR', false); } }

  async function loadCustProfile(c) {
    setSelectedCustomer(c);
    setL('profile', true);
    try {
      const [ch, inv, su, pm] = await Promise.all([
        window.zignagenixAPI.stripeGetCustomerCharges(c.id, 10),
        window.zignagenixAPI.stripeGetCustomerInvoices(c.id, 10),
        window.zignagenixAPI.stripeGetCustomerSubscriptions(c.id),
        window.zignagenixAPI.stripeGetPaymentMethods(c.id),
      ]);
      setCustProfile({
        charges: ch?.charges || [],
        invoices: inv?.invoices || [],
        subs: su?.subscriptions || [],
        methods: pm?.paymentMethods || [],
      });
    } catch (err) { console.error("[StripePage] Load error:", err.message); } finally { setL('profile', false); }
  }

  useEffect(() => {
    if (!connected) return;
    switch (tab) {
      case 'dashboard': loadDash(); break;
      case 'customers': loadCust(); break;
      case 'charges': loadCharges(); break;
      case 'invoices': loadInv(undefined, invFilter); break;
      case 'subscriptions': loadSubs(undefined, subFilter); break;
      case 'refunds': loadRef(); break;
      case 'products': loadProd(); break;
      case 'payouts': loadPay(); break;
      case 'tax': loadTax(); break;
      case 'identity': loadVer(verFilter); break;
      case 'fraud': loadDisp(); loadFraud(); break;
      case 'reports': loadBal(undefined, balType); loadRepTypes(); loadRepRuns(); break;
    }
  }, [tab, connected]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'charges', label: 'Charges', icon: CreditCard },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'subscriptions', label: 'Subscriptions', icon: RefreshCw },
    { id: 'refunds', label: 'Refunds', icon: RotateCcw },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'payouts', label: 'Payouts', icon: Wallet },
    { id: 'tax', label: 'Tax', icon: Percent },
    { id: 'identity', label: 'Identity', icon: Fingerprint },
    { id: 'fraud', label: 'Trust & Safety', icon: Shield },
    { id: 'reports', label: 'Reports', icon: FileBarChart },
    { id: 'settings', label: 'Settings', icon: Link2 },
  ];

  const Spin = () => <Loader2 size={24} className="spin" />;
  const LoadMore = ({ hasMore, list, onClick }) => hasMore ? (
    <div className="stripe-pagination"><button onClick={() => { const last = list[list.length - 1]; if (last) onClick(last.id); }}>Load More <ChevronRight size={16} /></button></div>
  ) : null;

  return (
    <div className="page stripe-page">
      <div className="stripe-header">
        <div>
          <h1 className="page-title">Stripe Payments</h1>
          <p className="page-subtitle">Payments, billing, tax, identity verification, fraud protection & reporting</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PrintButton pageTitle="Stripe Payments" subtitle="Payments, Billing, Tax & Fraud Protection" />
          <div className="stripe-conn-badge">
            {connected ? <span className="stripe-badge stripe-badge--ok"><CheckCircle size={14} /> Connected</span>
            : <span className="stripe-badge stripe-badge--err"><XCircle size={14} /> Disconnected</span>}
          </div>
        </div>
      </div>

      <DraggableTabBar
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
        prefKey="stripe_tab_order"
        className="stripe-tabs"
        tabClassName="stripe-tab"
        activeClassName="stripe-tab--active"
        iconSize={13}
      />

      <div className="stripe-content">

        {/* ═══ DASHBOARD ═══ */}
        {tab === 'dashboard' && (
          <div className="stripe-section">
            {loading.dash ? <div className="stripe-loading"><Spin /> Loading dashboard...</div> : summary ? (<>
              <div className="stripe-metric-grid">
                <div className="stripe-metric"><DollarSign size={22} /><div className="stripe-metric-value">{fmt(summary.availableBalance)}</div><div className="stripe-metric-label">Available Balance</div></div>
                <div className="stripe-metric"><Clock size={22} /><div className="stripe-metric-value">{fmt(summary.pendingBalance)}</div><div className="stripe-metric-label">Pending Balance</div></div>
                <div className="stripe-metric"><CreditCard size={22} /><div className="stripe-metric-value">{summary.recentCharges?.length || 0}</div><div className="stripe-metric-label">Recent Charges</div></div>
                <div className="stripe-metric"><Wallet size={22} /><div className="stripe-metric-value">{summary.recentPayouts?.length || 0}</div><div className="stripe-metric-label">Recent Payouts</div></div>
              </div>
              <div className="stripe-card">
                <h3 className="stripe-card-title"><TrendingUp size={18} /> Recent Charges</h3>
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Customer</th><th>Date</th></tr></thead>
                  <tbody>{(summary.recentCharges || []).map(c => (
                    <tr key={c.id}><td className="stripe-td-mono">{c.id.slice(-8)}</td><td className="stripe-td-money">{fmt(c.amount)}</td>
                    <td><span className={`stripe-status stripe-status--${c.status}`}>{c.status}</span></td>
                    <td>{c.billing_details?.name || c.customer || '—'}</td><td>{fmtD(c.created)}</td></tr>
                  ))}</tbody>
                </table></div>
              </div>
            </>) : <div className="stripe-empty-state"><BarChart3 size={48} /><p>Connect to Stripe to see your payment dashboard</p></div>}
          </div>
        )}

        {/* ═══ CUSTOMERS ═══ */}
        {tab === 'customers' && !selectedCustomer && (
          <div className="stripe-section">
            <div className="stripe-toolbar">
              <div className="stripe-search-bar">
                <Search size={16} />
                <input placeholder="Search by name or email..." value={custSearch} onChange={e => setCustSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchCust()} />
                <button className="btn-sm btn-primary" onClick={searchCust} disabled={loading.custS}>{loading.custS ? <Loader2 size={14} className="spin" /> : 'Search'}</button>
                {custSearchRes && <button className="btn-sm btn-ghost" onClick={() => { setCustSearchRes(null); setCustSearch(''); }}>Clear</button>}
              </div>
              <button className="btn-sm btn-ghost" onClick={() => loadCust()} disabled={loading.cust}><RefreshCw size={14} className={loading.cust ? 'spin' : ''} /> Refresh</button>
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Users size={18} /> {custSearchRes ? 'Search Results' : 'Customers'}</h3>
              {loading.cust ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Created</th><th>Profile</th></tr></thead>
                  <tbody>{(custSearchRes || customers).map(c => (
                    <tr key={c.id}>
                      <td className="stripe-td-name">{c.name || '—'}</td><td>{c.email || '—'}</td><td>{c.phone || '—'}</td><td>{fmtD(c.created)}</td>
                      <td><button className="stripe-link" onClick={() => loadCustProfile(c)}><Eye size={14} /></button></td>
                    </tr>
                  ))}
                  {(custSearchRes || customers).length === 0 && <tr><td colSpan="5" className="stripe-empty">No customers</td></tr>}
                  </tbody>
                </table></div>
              )}
              {!custSearchRes && <LoadMore hasMore={custMore} list={customers} onClick={(id) => loadCust(id)} />}
            </div>
          </div>
        )}

        {/* ═══ CUSTOMER PROFILE ═══ */}
        {tab === 'customers' && selectedCustomer && (
          <div className="stripe-section">
            <button className="btn-sm btn-ghost" onClick={() => setSelectedCustomer(null)} style={{ alignSelf: 'flex-start' }}>← Back to Customers</button>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><UserCheck size={18} /> Customer Profile</h3>
              <div className="stripe-profile-header">
                <div className="stripe-profile-field"><strong>Name:</strong> {selectedCustomer.name || '—'}</div>
                <div className="stripe-profile-field"><strong>Email:</strong> {selectedCustomer.email || '—'}</div>
                <div className="stripe-profile-field"><strong>Phone:</strong> {selectedCustomer.phone || '—'}</div>
                <div className="stripe-profile-field"><strong>ID:</strong> <code>{selectedCustomer.id}</code></div>
                <div className="stripe-profile-field"><strong>Created:</strong> {fmtDT(selectedCustomer.created)}</div>
              </div>
            </div>
            {loading.profile ? <div className="stripe-loading"><Spin /> Loading profile...</div> : (<>
              <div className="stripe-profile-grid">
                <div className="stripe-card">
                  <h3 className="stripe-card-title"><CreditCard size={16} /> Payment Methods ({custProfile.methods.length})</h3>
                  {custProfile.methods.length === 0 ? <p className="stripe-empty">No payment methods on file</p> : (
                    <div className="stripe-pm-list">{custProfile.methods.map(m => (
                      <div key={m.id} className="stripe-pm-item">
                        <CreditCard size={16} /> {m.card?.brand?.toUpperCase()} •••• {m.card?.last4} — Exp {m.card?.exp_month}/{m.card?.exp_year}
                      </div>
                    ))}</div>
                  )}
                </div>
                <div className="stripe-card">
                  <h3 className="stripe-card-title"><RefreshCw size={16} /> Subscriptions ({custProfile.subs.length})</h3>
                  {custProfile.subs.length === 0 ? <p className="stripe-empty">No subscriptions</p> : (
                    <div className="stripe-table-wrap"><table className="stripe-table">
                      <thead><tr><th>Status</th><th>Amount</th><th>Interval</th><th>Period End</th></tr></thead>
                      <tbody>{custProfile.subs.map(s => (
                        <tr key={s.id}>
                          <td><span className={`stripe-status stripe-status--${s.status}`}>{s.status}</span></td>
                          <td className="stripe-td-money">{s.items?.data?.[0]?.price ? fmt(s.items.data[0].price.unit_amount) : '—'}</td>
                          <td>{s.items?.data?.[0]?.price?.recurring?.interval || '—'}</td>
                          <td>{fmtD(s.current_period_end)}</td>
                        </tr>
                      ))}</tbody>
                    </table></div>
                  )}
                </div>
              </div>
              <div className="stripe-profile-grid">
                <div className="stripe-card">
                  <h3 className="stripe-card-title"><CreditCard size={16} /> Recent Charges ({custProfile.charges.length})</h3>
                  {custProfile.charges.length === 0 ? <p className="stripe-empty">No charges</p> : (
                    <div className="stripe-table-wrap"><table className="stripe-table">
                      <thead><tr><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                      <tbody>{custProfile.charges.map(c => (
                        <tr key={c.id}><td className="stripe-td-money">{fmt(c.amount)}</td>
                        <td><span className={`stripe-status stripe-status--${c.status}`}>{c.status}</span></td>
                        <td>{fmtD(c.created)}</td></tr>
                      ))}</tbody>
                    </table></div>
                  )}
                </div>
                <div className="stripe-card">
                  <h3 className="stripe-card-title"><FileText size={16} /> Recent Invoices ({custProfile.invoices.length})</h3>
                  {custProfile.invoices.length === 0 ? <p className="stripe-empty">No invoices</p> : (
                    <div className="stripe-table-wrap"><table className="stripe-table">
                      <thead><tr><th>Number</th><th>Amount</th><th>Status</th><th>Due</th></tr></thead>
                      <tbody>{custProfile.invoices.map(i => (
                        <tr key={i.id}><td className="stripe-td-mono">{i.number || '—'}</td>
                        <td className="stripe-td-money">{fmt(i.amount_due || 0)}</td>
                        <td><span className={`stripe-status stripe-status--${i.status}`}>{i.status}</span></td>
                        <td>{fmtD(i.due_date)}</td></tr>
                      ))}</tbody>
                    </table></div>
                  )}
                </div>
              </div>
            </>)}
          </div>
        )}

        {/* ═══ CHARGES ═══ */}
        {tab === 'charges' && (
          <div className="stripe-section">
            <div className="stripe-toolbar"><button className="btn-sm btn-ghost" onClick={() => loadCharges()} disabled={loading.chg}><RefreshCw size={14} className={loading.chg ? 'spin' : ''} /> Refresh</button></div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><CreditCard size={18} /> Charges</h3>
              {loading.chg ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Customer</th><th>Description</th><th>Date</th></tr></thead>
                  <tbody>{charges.map(c => (
                    <tr key={c.id}><td className="stripe-td-mono">{c.id.slice(-8)}</td><td className="stripe-td-money">{fmt(c.amount)}</td>
                    <td><span className={`stripe-status stripe-status--${c.status}`}>{c.status}</span></td>
                    <td>{c.billing_details?.name || c.customer || '—'}</td><td className="stripe-td-desc">{c.description || '—'}</td><td>{fmtD(c.created)}</td></tr>
                  ))}{charges.length === 0 && <tr><td colSpan="6" className="stripe-empty">No charges</td></tr>}</tbody>
                </table></div>
              )}
              <LoadMore hasMore={chargesMore} list={charges} onClick={(id) => loadCharges(id)} />
            </div>
          </div>
        )}

        {/* ═══ INVOICES ═══ */}
        {tab === 'invoices' && (
          <div className="stripe-section">
            <div className="stripe-toolbar">
              <div className="stripe-filter"><label>Status:</label>
                <select value={invFilter} onChange={e => { setInvFilter(e.target.value); loadInv(undefined, e.target.value); }}>
                  <option value="">All</option><option value="draft">Draft</option><option value="open">Open</option><option value="paid">Paid</option><option value="void">Void</option><option value="uncollectible">Uncollectible</option>
                </select></div>
              <button className="btn-sm btn-ghost" onClick={() => loadInv(undefined, invFilter)} disabled={loading.inv}><RefreshCw size={14} className={loading.inv ? 'spin' : ''} /> Refresh</button>
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><FileText size={18} /> Invoices</h3>
              {loading.inv ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>Number</th><th>Customer</th><th>Amount</th><th>Status</th><th>Due</th><th>Link</th></tr></thead>
                  <tbody>{invoices.map(i => (
                    <tr key={i.id}><td className="stripe-td-mono">{i.number || i.id.slice(-8)}</td><td>{i.customer_name || i.customer_email || '—'}</td>
                    <td className="stripe-td-money">{fmt(i.amount_due || 0)}</td>
                    <td><span className={`stripe-status stripe-status--${i.status}`}>{i.status}</span></td>
                    <td>{fmtD(i.due_date)}</td>
                    <td>{i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="stripe-link"><ExternalLink size={14} /></a>}</td></tr>
                  ))}{invoices.length === 0 && <tr><td colSpan="6" className="stripe-empty">No invoices</td></tr>}</tbody>
                </table></div>
              )}
              <LoadMore hasMore={invMore} list={invoices} onClick={(id) => loadInv(id, invFilter)} />
            </div>
          </div>
        )}

        {/* ═══ SUBSCRIPTIONS ═══ */}
        {tab === 'subscriptions' && (
          <div className="stripe-section">
            <div className="stripe-toolbar">
              <div className="stripe-filter"><label>Status:</label>
                <select value={subFilter} onChange={e => { setSubFilter(e.target.value); loadSubs(undefined, e.target.value); }}>
                  <option value="">All</option><option value="active">Active</option><option value="past_due">Past Due</option><option value="canceled">Canceled</option><option value="trialing">Trialing</option>
                </select></div>
              <button className="btn-sm btn-ghost" onClick={() => loadSubs(undefined, subFilter)} disabled={loading.sub}><RefreshCw size={14} className={loading.sub ? 'spin' : ''} /> Refresh</button>
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><RefreshCw size={18} /> Subscriptions</h3>
              {loading.sub ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Customer</th><th>Status</th><th>Amount</th><th>Interval</th><th>Period End</th></tr></thead>
                  <tbody>{subs.map(s => (
                    <tr key={s.id}><td className="stripe-td-mono">{s.id.slice(-8)}</td><td>{s.customer || '—'}</td>
                    <td><span className={`stripe-status stripe-status--${s.status}`}>{s.status}</span></td>
                    <td className="stripe-td-money">{s.items?.data?.[0]?.price ? fmt(s.items.data[0].price.unit_amount) : '—'}</td>
                    <td>{s.items?.data?.[0]?.price?.recurring?.interval || '—'}</td><td>{fmtD(s.current_period_end)}</td></tr>
                  ))}{subs.length === 0 && <tr><td colSpan="6" className="stripe-empty">No subscriptions</td></tr>}</tbody>
                </table></div>
              )}
              <LoadMore hasMore={subsMore} list={subs} onClick={(id) => loadSubs(id, subFilter)} />
            </div>
          </div>
        )}

        {/* ═══ REFUNDS ═══ */}
        {tab === 'refunds' && (
          <div className="stripe-section">
            <div className="stripe-toolbar"><button className="btn-sm btn-ghost" onClick={() => loadRef()} disabled={loading.ref}><RefreshCw size={14} className={loading.ref ? 'spin' : ''} /> Refresh</button></div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><RotateCcw size={18} /> Refunds</h3>
              {loading.ref ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Reason</th><th>Charge</th><th>Date</th></tr></thead>
                  <tbody>{refunds.map(r => (
                    <tr key={r.id}><td className="stripe-td-mono">{r.id.slice(-8)}</td><td className="stripe-td-money">{fmt(r.amount)}</td>
                    <td><span className={`stripe-status stripe-status--${r.status}`}>{r.status}</span></td>
                    <td>{r.reason || '—'}</td><td className="stripe-td-mono">{r.charge ? String(r.charge).slice(-8) : '—'}</td><td>{fmtD(r.created)}</td></tr>
                  ))}{refunds.length === 0 && <tr><td colSpan="6" className="stripe-empty">No refunds</td></tr>}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}

        {/* ═══ PRODUCTS ═══ */}
        {tab === 'products' && (
          <div className="stripe-section">
            <div className="stripe-toolbar"><button className="btn-sm btn-ghost" onClick={loadProd} disabled={loading.prod}><RefreshCw size={14} className={loading.prod ? 'spin' : ''} /> Refresh</button></div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Package size={18} /> Products</h3>
              {loading.prod ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>Name</th><th>Description</th><th>Active</th><th>Created</th></tr></thead>
                  <tbody>{products.map(p => (
                    <tr key={p.id}><td className="stripe-td-name">{p.name || '—'}</td><td className="stripe-td-desc">{p.description || '—'}</td>
                    <td>{p.active ? <CheckCircle size={16} className="text-green" /> : <XCircle size={16} className="text-red" />}</td><td>{fmtD(p.created)}</td></tr>
                  ))}{products.length === 0 && <tr><td colSpan="4" className="stripe-empty">No products</td></tr>}</tbody>
                </table></div>
              )}
            </div>
          </div>
        )}

        {/* ═══ PAYOUTS ═══ */}
        {tab === 'payouts' && (
          <div className="stripe-section">
            <div className="stripe-toolbar"><button className="btn-sm btn-ghost" onClick={() => loadPay()} disabled={loading.pay}><RefreshCw size={14} className={loading.pay ? 'spin' : ''} /> Refresh</button></div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Wallet size={18} /> Payouts</h3>
              {loading.pay ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Method</th><th>Arrival</th></tr></thead>
                  <tbody>{payouts.map(p => (
                    <tr key={p.id}><td className="stripe-td-mono">{p.id.slice(-8)}</td><td className="stripe-td-money">{fmt(p.amount)}</td>
                    <td><span className={`stripe-status stripe-status--${p.status}`}>{p.status}</span></td><td>{p.method || '—'}</td><td>{fmtD(p.arrival_date)}</td></tr>
                  ))}{payouts.length === 0 && <tr><td colSpan="5" className="stripe-empty">No payouts</td></tr>}</tbody>
                </table></div>
              )}
              <LoadMore hasMore={payMore} list={payouts} onClick={(id) => loadPay(id)} />
            </div>
          </div>
        )}

        {/* ═══ TAX ═══ */}
        {tab === 'tax' && (
          <div className="stripe-section">
            <div className="stripe-toolbar"><button className="btn-sm btn-ghost" onClick={loadTax} disabled={loading.tax}><RefreshCw size={14} className={loading.tax ? 'spin' : ''} /> Refresh</button></div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Percent size={18} /> Tax Rates</h3>
              <p className="stripe-card-desc">Active tax rates configured in Stripe. Tax calculations, tax IDs per customer, and automatic tax collection are all supported.</p>
              {loading.tax ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>Name</th><th>Rate</th><th>Inclusive</th><th>Jurisdiction</th><th>Type</th><th>Active</th></tr></thead>
                  <tbody>{taxRates.map(t => (
                    <tr key={t.id}><td className="stripe-td-name">{t.display_name}</td><td><strong>{t.percentage}%</strong></td>
                    <td>{t.inclusive ? 'Yes' : 'No'}</td><td>{t.jurisdiction || '—'}</td><td>{t.tax_type || '—'}</td>
                    <td>{t.active ? <CheckCircle size={16} className="text-green" /> : <XCircle size={16} className="text-red" />}</td></tr>
                  ))}{taxRates.length === 0 && <tr><td colSpan="6" className="stripe-empty">No tax rates configured</td></tr>}</tbody>
                </table></div>
              )}
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><FileText size={18} /> Tax Capabilities</h3>
              <div className="stripe-caps-grid">
                <div className="stripe-cap"><Percent size={18} /> Tax Rates</div>
                <div className="stripe-cap"><Users size={18} /> Customer Tax IDs</div>
                <div className="stripe-cap"><DollarSign size={18} /> Tax Calculations</div>
                <div className="stripe-cap"><FileText size={18} /> Automatic Tax on Invoices</div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ IDENTITY ═══ */}
        {tab === 'identity' && (
          <div className="stripe-section">
            <div className="stripe-toolbar">
              <div className="stripe-filter"><label>Status:</label>
                <select value={verFilter} onChange={e => { setVerFilter(e.target.value); loadVer(e.target.value); }}>
                  <option value="">All</option><option value="requires_input">Requires Input</option><option value="processing">Processing</option><option value="verified">Verified</option><option value="canceled">Canceled</option>
                </select></div>
              <button className="btn-sm btn-ghost" onClick={() => loadVer(verFilter)} disabled={loading.ver}><RefreshCw size={14} className={loading.ver ? 'spin' : ''} /> Refresh</button>
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Fingerprint size={18} /> Identity Verifications</h3>
              <p className="stripe-card-desc">Stripe Identity verifies patient and user identities using government-issued IDs and selfie matching to prevent fraud.</p>
              {loading.ver ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Status</th><th>Type</th><th>Created</th><th>Last Error</th></tr></thead>
                  <tbody>{verifications.map(v => (
                    <tr key={v.id}><td className="stripe-td-mono">{v.id.slice(-12)}</td>
                    <td><span className={`stripe-status stripe-status--${v.status === 'verified' ? 'succeeded' : v.status === 'canceled' ? 'canceled' : 'pending'}`}>{v.status}</span></td>
                    <td>{v.type || '—'}</td><td>{fmtDT(v.created)}</td>
                    <td className="stripe-td-desc">{v.last_error?.reason || '—'}</td></tr>
                  ))}{verifications.length === 0 && <tr><td colSpan="5" className="stripe-empty">No verification sessions</td></tr>}</tbody>
                </table></div>
              )}
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Shield size={18} /> Verification Capabilities</h3>
              <div className="stripe-caps-grid">
                <div className="stripe-cap"><Fingerprint size={18} /> Document Verification</div>
                <div className="stripe-cap"><UserCheck size={18} /> Selfie Matching</div>
                <div className="stripe-cap"><FileText size={18} /> ID Card / Passport / License</div>
                <div className="stripe-cap"><Eye size={18} /> Verification Reports</div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ TRUST & SAFETY ═══ */}
        {tab === 'fraud' && (
          <div className="stripe-section">
            <div className="stripe-toolbar"><button className="btn-sm btn-ghost" onClick={() => { loadDisp(); loadFraud(); }} disabled={loading.disp}><RefreshCw size={14} className={loading.disp ? 'spin' : ''} /> Refresh</button></div>

            <div className="stripe-card">
              <h3 className="stripe-card-title"><AlertTriangle size={18} /> Disputes</h3>
              {loading.disp ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Status</th><th>Reason</th><th>Charge</th><th>Created</th></tr></thead>
                  <tbody>{disputes.map(d => (
                    <tr key={d.id}><td className="stripe-td-mono">{d.id.slice(-8)}</td><td className="stripe-td-money">{fmt(d.amount)}</td>
                    <td><span className={`stripe-status stripe-status--${d.status === 'won' ? 'succeeded' : d.status === 'lost' ? 'failed' : 'pending'}`}>{d.status}</span></td>
                    <td>{d.reason || '—'}</td><td className="stripe-td-mono">{d.charge ? String(d.charge).slice(-8) : '—'}</td><td>{fmtD(d.created)}</td></tr>
                  ))}{disputes.length === 0 && <tr><td colSpan="6" className="stripe-empty">No disputes — that's great!</td></tr>}</tbody>
                </table></div>
              )}
              <LoadMore hasMore={dispMore} list={disputes} onClick={(id) => loadDisp(id)} />
            </div>

            <div className="stripe-card">
              <h3 className="stripe-card-title"><Shield size={18} /> Early Fraud Warnings (Radar)</h3>
              {loading.fraud ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Fraud Type</th><th>Charge</th><th>Created</th></tr></thead>
                  <tbody>{fraudWarnings.map(w => (
                    <tr key={w.id}><td className="stripe-td-mono">{w.id.slice(-8)}</td><td>{w.fraud_type || '—'}</td>
                    <td className="stripe-td-mono">{w.charge ? String(w.charge).slice(-8) : '—'}</td><td>{fmtD(w.created)}</td></tr>
                  ))}{fraudWarnings.length === 0 && <tr><td colSpan="4" className="stripe-empty">No fraud warnings</td></tr>}</tbody>
                </table></div>
              )}
            </div>

            <div className="stripe-card">
              <h3 className="stripe-card-title"><Shield size={18} /> Radar Trust & Safety</h3>
              <div className="stripe-caps-grid">
                <div className="stripe-cap"><AlertTriangle size={18} /> Dispute Management</div>
                <div className="stripe-cap"><Shield size={18} /> Early Fraud Warnings</div>
                <div className="stripe-cap"><Ban size={18} /> Block Lists</div>
                <div className="stripe-cap"><FileText size={18} /> Evidence Submission</div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ REPORTS ═══ */}
        {tab === 'reports' && (
          <div className="stripe-section">
            <div className="stripe-toolbar">
              <div className="stripe-filter"><label>Txn Type:</label>
                <select value={balType} onChange={e => { setBalType(e.target.value); loadBal(undefined, e.target.value); }}>
                  <option value="">All</option><option value="charge">Charges</option><option value="refund">Refunds</option><option value="payout">Payouts</option><option value="adjustment">Adjustments</option><option value="stripe_fee">Fees</option>
                </select></div>
              <button className="btn-sm btn-ghost" onClick={() => { loadBal(undefined, balType); loadRepRuns(); }} disabled={loading.bal}><RefreshCw size={14} className={loading.bal ? 'spin' : ''} /> Refresh</button>
            </div>

            <div className="stripe-card">
              <h3 className="stripe-card-title"><FileBarChart size={18} /> Balance Transactions</h3>
              {loading.bal ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Amount</th><th>Net</th><th>Fee</th><th>Type</th><th>Description</th><th>Date</th></tr></thead>
                  <tbody>{balTxns.map(t => (
                    <tr key={t.id}><td className="stripe-td-mono">{t.id.slice(-8)}</td>
                    <td className="stripe-td-money">{fmt(t.amount)}</td><td className="stripe-td-money">{fmt(t.net)}</td>
                    <td style={{ color: '#DC3545' }}>{fmt(t.fee)}</td>
                    <td><span className="stripe-status stripe-status--pending">{t.type}</span></td>
                    <td className="stripe-td-desc">{t.description || '—'}</td><td>{fmtD(t.created)}</td></tr>
                  ))}{balTxns.length === 0 && <tr><td colSpan="7" className="stripe-empty">No transactions</td></tr>}</tbody>
                </table></div>
              )}
              <LoadMore hasMore={balMore} list={balTxns} onClick={(id) => loadBal(id, balType)} />
            </div>

            <div className="stripe-card">
              <h3 className="stripe-card-title"><FileText size={18} /> Report Runs</h3>
              {loading.repR ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-table-wrap"><table className="stripe-table">
                  <thead><tr><th>ID</th><th>Report Type</th><th>Status</th><th>Created</th><th>Download</th></tr></thead>
                  <tbody>{reportRuns.map(r => (
                    <tr key={r.id}><td className="stripe-td-mono">{r.id.slice(-8)}</td><td>{r.report_type}</td>
                    <td><span className={`stripe-status stripe-status--${r.status === 'succeeded' ? 'succeeded' : 'pending'}`}>{r.status}</span></td>
                    <td>{fmtDT(r.created)}</td>
                    <td>{r.result?.url ? <a href={r.result.url} target="_blank" rel="noopener noreferrer" className="stripe-link"><ExternalLink size={14} /></a> : '—'}</td></tr>
                  ))}{reportRuns.length === 0 && <tr><td colSpan="5" className="stripe-empty">No report runs</td></tr>}</tbody>
                </table></div>
              )}
            </div>

            <div className="stripe-card">
              <h3 className="stripe-card-title"><BarChart3 size={18} /> Available Report Types ({reportTypes.length})</h3>
              {loading.repT ? <div className="stripe-loading"><Spin /> Loading...</div> : (
                <div className="stripe-report-types">
                  {reportTypes.map(t => (
                    <div key={t.id} className="stripe-report-type-item">
                      <span className="stripe-report-type-name">{t.name || t.id}</span>
                      <span className="stripe-report-type-id"><code>{t.id}</code></span>
                    </div>
                  ))}
                  {reportTypes.length === 0 && <p className="stripe-empty">No report types available</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ SETTINGS ═══ */}
        {tab === 'settings' && (
          <div className="stripe-section">
            <div className="stripe-card">
              <h3 className="stripe-card-title"><Link2 size={18} /> Connection Settings</h3>
              <p className="stripe-card-desc">Add your Stripe API keys to the <code>.env</code> file. Use the secret key (starts with <code>sk_</code>) for server-side operations.</p>
              <div className="stripe-config-box">
                <div className="stripe-config-item"><span className="stripe-config-key">STRIPE_SECRET_KEY</span><span className="stripe-config-val">{process.env.STRIPE_SECRET_KEY ? '••••••••' + (process.env.STRIPE_SECRET_KEY || '').slice(-8) : 'Not set'}</span></div>
                <div className="stripe-config-item"><span className="stripe-config-key">STRIPE_PUBLISHABLE_KEY</span><span className="stripe-config-val">{process.env.STRIPE_PUBLISHABLE_KEY ? '••••••••' + (process.env.STRIPE_PUBLISHABLE_KEY || '').slice(-8) : 'Not set'}</span></div>
              </div>
              <button className="btn-primary stripe-test-btn" onClick={() => testConn(false)} disabled={connecting}>
                {connecting ? <><Loader2 size={16} className="spin" /> Testing...</> : <><Zap size={16} /> Test Connection</>}
              </button>
              {connErr && <div className="stripe-error">{connErr}</div>}
              {connected && <div className="stripe-success"><CheckCircle size={16} /> Connected to Stripe API</div>}
            </div>
            <div className="stripe-card">
              <h3 className="stripe-card-title"><ShoppingCart size={18} /> Full Capabilities</h3>
              <div className="stripe-caps-grid">
                <div className="stripe-cap"><Users size={18} /> Customer Profiles</div>
                <div className="stripe-cap"><CreditCard size={18} /> Charges &amp; Payments</div>
                <div className="stripe-cap"><FileText size={18} /> Invoices &amp; Billing</div>
                <div className="stripe-cap"><RefreshCw size={18} /> Subscriptions</div>
                <div className="stripe-cap"><RotateCcw size={18} /> Refunds</div>
                <div className="stripe-cap"><Package size={18} /> Products &amp; Prices</div>
                <div className="stripe-cap"><Wallet size={18} /> Payouts</div>
                <div className="stripe-cap"><ShoppingCart size={18} /> Checkout Sessions</div>
                <div className="stripe-cap"><Percent size={18} /> Tax Rates &amp; IDs</div>
                <div className="stripe-cap"><Fingerprint size={18} /> Identity Verification</div>
                <div className="stripe-cap"><Shield size={18} /> Radar Fraud Protection</div>
                <div className="stripe-cap"><AlertTriangle size={18} /> Dispute Management</div>
                <div className="stripe-cap"><FileBarChart size={18} /> Financial Reports</div>
                <div className="stripe-cap"><DollarSign size={18} /> Balance Transactions</div>
              </div>
            </div>
          </div>
        )}

        {!connected && !['settings'].includes(tab) && (
          <div className="stripe-disconnected"><XCircle size={24} /><p>Not connected to Stripe. Go to the <strong>Settings</strong> tab to configure your API key.</p></div>
        )}
      </div>
    </div>
  );
}

export default StripePage;
